import React, { useCallback, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/shadcn/dialog";
import { RichTextValue } from "../RichTextField";
import TiptapEditor from "./TiptapEditor";
import { Button } from "@/components/shadcn/button";
import { Check, Sparkles, Undo2 } from "lucide-react";
import { Separator } from "@/components/shadcn/separator";
import { HeuristicsSection } from "./HeuristicsSection";
import { HeuristicEvaluationResponse } from "@/generated";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { type AdvancedChipOption } from "./chipOptionsConfig";

interface AIDialogEditorProps {
  isOpen: boolean;
  onOpenChange: (value: boolean) => void;
  content: RichTextValue[];
  onSave: (content: RichTextValue[]) => void;
  chipOptions?: AdvancedChipOption[];
  unselectAllNodes?: () => any;
  label?: string;
  isControlled?: boolean;
  enableHeuristics?: boolean;
  initialHeuristicsOpen?: boolean;
  heuristicResults?: HeuristicEvaluationResponse | null;
  isLoadingHeuristics?: boolean;
  isHeuristicsOutdated?: boolean;
  isHeuristicsError?: boolean;
  heuristicsErrorMessage?: string | null;
  canAnalyseHeuristics?: boolean;
  triggerHeuristicEvaluation?: () => void;
  scheduleHeuristicReanalysis?: () => void;
  revertHeuristicAnalysis?: () => void;
  onHeuristicTextChange?: (text: string | null) => void;
  improverContext?: string;
}

const AIDialogEditor: React.FC<AIDialogEditorProps> = ({
  isOpen,
  onOpenChange,
  content,
  onSave,
  chipOptions,
  unselectAllNodes,
  label,
  isControlled,
  enableHeuristics,
  initialHeuristicsOpen = true,
  heuristicResults,
  isLoadingHeuristics,
  isHeuristicsOutdated,
  isHeuristicsError,
  heuristicsErrorMessage,
  canAnalyseHeuristics,
  triggerHeuristicEvaluation,
  scheduleHeuristicReanalysis,
  revertHeuristicAnalysis,
  onHeuristicTextChange,
  improverContext,
}) => {
  const [editedContent, setEditedContent] =
    React.useState<RichTextValue[]>(content);
  const [isHeuristicsOpen, setIsHeuristicsOpen] = useState(
    initialHeuristicsOpen
  );

  const usedValue =
    editedContent.length > 0
      ? editedContent[editedContent.length - 1]
      : undefined;

  const hasPendingRefinement = editedContent.length > 1;
  const heuristicsPanelOpen = Boolean(enableHeuristics && isHeuristicsOpen);

  // Sync dialog text to parent's heuristic hook via text override
  useEffect(() => {
    if (isOpen) {
      onHeuristicTextChange?.(usedValue?.text || "");
    }
  }, [usedValue?.text, isOpen, onHeuristicTextChange]);

  const handleEditorChange = useCallback((val: RichTextValue) => {
    setEditedContent((prev) => {
      if (prev.length === 0) return [val];
      return prev.map((v, i) => (i === prev.length - 1 ? val : v));
    });
  }, []);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      const contentToSave =
        editedContent.length > 1
          ? [editedContent[editedContent.length - 1]]
          : editedContent;

      setEditedContent(contentToSave);
      onSave(contentToSave);
      onHeuristicTextChange?.(null);
    }
    onOpenChange(open);
  };

  const handleAccept = () => {
    if (editedContent.length > 1) {
      setEditedContent([editedContent[editedContent.length - 1]]);
    }
  };

  const handleRevert = () => {
    if (editedContent.length > 1) {
      const prev = editedContent.slice(0, -1);
      setEditedContent(prev);
      revertHeuristicAnalysis?.();
    }
  };

  const handleAppendRefinement = useCallback((value: RichTextValue) => {
    setEditedContent((prev) => [...prev, value]);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsHeuristicsOpen(initialHeuristicsOpen);
    }
  }, [initialHeuristicsOpen, isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal>
      <DialogContent
        className="bg-background flex flex-col h-[85%] w-[85%] max-w-[85%]! p-0"
        stopPropagation
        onEscapeKeyDown={(e) => e.stopPropagation()}
        onInteractOutside={(e) => {
          if ((e.target as Element)?.closest("[data-sonner-toaster]")) {
            e.preventDefault();
          }
        }}
        showCloseButton={true}
        headerButtonsClassName="top-4"
      >
        {/* Dialog title */}
        <DialogHeader className="flex flex-row flex-shrink-0 items-center justify-between px-6 py-4 border-b border-border h-12.5">
          <DialogTitle>{label || "Prompt editor"}</DialogTitle>
          <div className="flex gap-4 mr-7">
            <NoxusTooltip content="Toggle prompt optimizer">
              <Button
                onClick={() => setIsHeuristicsOpen(!isHeuristicsOpen)}
                variant="ghost"
                size="iconsm"
                aria-label="Toggle prompt optimizer"
                disabled={!enableHeuristics}
                className={cn(
                  isHeuristicsOpen && enableHeuristics && "bg-accent"
                )}
              >
                <Sparkles size={16} strokeWidth={1.33} />
              </Button>
            </NoxusTooltip>
            <Separator orientation="vertical" className="h-6 my-auto" />
          </div>
        </DialogHeader>
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left panel - Editor (~60%) */}
          <div className="flex flex-col flex-1 min-w-0 p-6 pt-5 bg-background2 relative">
            <TiptapEditor
              key={editedContent.length}
              onChange={handleEditorChange}
              value={usedValue}
              chipOptions={chipOptions}
              unselectAllNodes={unselectAllNodes}
              fullSize={true}
              config={{ isExpandable: false, hideToolbar: false }}
              isControlled={isControlled}
              isColored={false}
              showMutedOverlay={hasPendingRefinement}
            />

            {hasPendingRefinement && (
              <div className="absolute bottom-9 right-9 z-10 flex items-center rounded-md border border-border bg-background shadow-sm h-fit">
                <Button
                  onClick={handleRevert}
                  className="border-none text-sm text-muted-foreground font-medium hover:text-foreground transition-colors hover:bg-transparent"
                  variant="ghost"
                >
                  <Undo2 size={16} />
                  Revert
                </Button>
                <div>
                  <Separator orientation="vertical" className="h-6 my-auto" />
                </div>
                <Button
                  onClick={handleAccept}
                  className="border-none text-sm text-muted-foreground font-medium hover:text-foreground transition-colors hover:bg-transparent"
                  variant="ghost"
                >
                  <Check size={16} color="var(--color-emerald-600)" />
                  Accept
                </Button>
              </div>
            )}
          </div>

          {/* Right panel - Heuristics (~40%) */}
          <motion.div
            aria-hidden={!heuristicsPanelOpen}
            data-testid="prompt-optimizer-panel"
            data-open={heuristicsPanelOpen}
            className="flex-shrink-0 overflow-hidden"
            initial={false}
            animate={{
              width: heuristicsPanelOpen ? "40%" : "0%",
              opacity: heuristicsPanelOpen ? 1 : 0,
              x: heuristicsPanelOpen ? 0 : 24,
            }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            style={{
              minWidth: heuristicsPanelOpen ? "27.5rem" : "0",
              pointerEvents: heuristicsPanelOpen ? "auto" : "none",
            }}
          >
            <div className="h-full border-l border-border flex flex-col overflow-hidden  min-w-25">
              <HeuristicsSection
                text={usedValue?.text || ""}
                onAppendRefinement={handleAppendRefinement}
                chipOptions={chipOptions}
                heuristicResults={heuristicResults ?? null}
                isLoadingHeuristics={isLoadingHeuristics ?? false}
                isOutdated={isHeuristicsOutdated ?? false}
                canAnalyse={canAnalyseHeuristics ?? false}
                triggerEvaluation={triggerHeuristicEvaluation}
                scheduleReanalysis={scheduleHeuristicReanalysis}
                improverContext={improverContext}
                isError={isHeuristicsError}
                errorMessage={heuristicsErrorMessage}
                retryEvaluation={triggerHeuristicEvaluation}
              />
            </div>
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIDialogEditor;
